export function calculateInstallmentPlan(input) {
  const {
    quoteDate,
    departureDate,
    planTotal,
    firstPaymentOffsetDays = 8,
    minDepositDollarFloor = 0,
    // Legacy name kept for compatibility; now governs minimum weeks for the minimum deposit (20% Slice-style base).
    minWeeksFor10Pct = 4,
    minWeeksForPlan = 2,
    maxWeeklyAmount = Infinity,
  } = input;

  const addDays = (d, n) => {
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    x.setUTCDate(x.getUTCDate() + n);
    return x;
  };

  const daysBetween = (a, b) =>
    Math.round((Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate()) -
                Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate())) / 86400000);

  const round2 = (n) => Math.round(n * 100) / 100;

  const today = new Date(Date.UTC(
    quoteDate.getUTCFullYear(), quoteDate.getUTCMonth(), quoteDate.getUTCDate()
  ));
  const dep = new Date(Date.UTC(
    departureDate.getUTCFullYear(), departureDate.getUTCMonth(), departureDate.getUTCDate()
  ));

  const daysToDeparture = daysBetween(today, dep);

  let cutoff = 20;
  if (daysToDeparture >= 240) cutoff = 90;
  else cutoff = 20;

  const firstPayDate = addDays(today, firstPaymentOffsetDays);

  const weeklySlots = (cut) => {
    const lastAllowed = addDays(dep, -cut);
    const totalDays = daysBetween(firstPayDate, lastAllowed);
    if (totalDays < 0) return 0;
    return Math.floor(totalDays / 7) + 1;
  };

  let weeks = weeklySlots(cutoff);
  if (weeks < minWeeksForPlan && daysToDeparture <= 60) {
    cutoff = 14;
    weeks = weeklySlots(cutoff);
  }

  if (weeks < minWeeksForPlan) {
    return {
      minDepositPct: "pay-in-full",
      depositDueToday: round2(planTotal),
      remainingAfterDeposit: 0,
      finalCutoffDays: cutoff,
    };
  }

  // Slice-style base: 20% minimum deposit for new installments (tiers 30/40/50 handled elsewhere).
  const minPct = 0.20;
  const minPctAmount = round2(planTotal * minPct);
  let minDeposit = minPct;

  // Count deposit as first payment when evaluating eligibility
  // (weeks + 1) represents total payments: deposit + weekly installments
  const totalPayments = weeks + 1;
  
  if (totalPayments >= minWeeksFor10Pct && minPctAmount >= minDepositDollarFloor) {
    minDeposit = 0.20;
  } else if (weeks >= 3) {
    minDeposit = 0.20;
  }

  const remainingIfMin = planTotal - round2(planTotal * minDeposit);
  const nominalWeekly = remainingIfMin / weeks;
  if (nominalWeekly > maxWeeklyAmount) {
    if ((planTotal - round2(planTotal * 0.30)) / weeks <= maxWeeklyAmount) {
      minDeposit = 0.30;
    } else if ((planTotal - round2(planTotal * 0.40)) / weeks <= maxWeeklyAmount) {
      minDeposit = 0.40;
    } else {
      minDeposit = 0.50;
    }
  }

  const depositDueToday = round2(planTotal * minDeposit);
  let remaining = round2(planTotal - depositDueToday);

  const buildSchedule = (cadence) => {
    const gap = cadence === "weekly" ? 7 : 14;
    const slots = cadence === "weekly" ? weeks : Math.max(1, Math.floor((weeks + 1) / 2));
    const nominal = round2(remaining / slots);

    const payments = [];
    let running = 0;
    for (let i = 0; i < slots; i++) {
      const date = addDays(firstPayDate, i * gap);
      let amount = (i < slots - 1) ? nominal : round2(remaining - running);
      running = round2(running + amount);
      payments.push({ 
        date: date.toISOString().slice(0, 10), 
        amount 
      });
    }

    return {
      cadence,
      payments,
      installmentsCount: slots,
      installmentAmount: nominal,
      firstPayment: payments[0].date,
      finalPayment: payments[payments.length - 1].date,
    };
  };

  const weekly = buildSchedule("weekly");
  const biweekly = buildSchedule("biweekly");

  return {
    minDepositPct: minDeposit,
    depositDueToday,
    remainingAfterDeposit: remaining,
    finalCutoffDays: cutoff,
    weekly,
    biweekly,
  };
}

export function calculateUpfrontPayment(planTotal, departureDate) {
  const MAX_WEEKLY_PAYMENT = 350;
  
  const today = new Date();
  const departure = new Date(departureDate);
  
  const plan = calculateInstallmentPlan({
    quoteDate: today,
    departureDate: departure,
    planTotal: planTotal,
    maxWeeklyAmount: MAX_WEEKLY_PAYMENT
  });
  
  if (plan.minDepositPct === "pay-in-full") {
    return {
      upfrontAmount: Math.round(planTotal),
      totalAmount: Math.round(planTotal),
      isPayInFull: true
    };
  }
  
  return {
    upfrontAmount: Math.round(plan.depositDueToday),
    totalAmount: Math.round(planTotal),
    isPayInFull: false,
    depositPercent: plan.minDepositPct
  };
}
